import type { JSX } from 'react';
import { useEffect, useState } from 'react';

import { Info, MessageSquarePlus, Search, ShieldAlert, User, Wrench } from 'lucide-react';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import { useLazyImportConversationQuery } from '../../api/conversations';
import type { ConversationImportCandidate, ConversationImportPreview } from '../../model/conversationImport';
import type { AgentTestTurn, AgentTestTurnRole } from '../../model/scenario';

const SEARCH_ERROR_FALLBACK = 'No se pudo buscar la conversación. Intenta nuevamente.';
const NOT_FOUND_MESSAGE = 'No se encontró un lead con ese ID o teléfono en este proyecto.';

const ROLE_CHIPS: Record<AgentTestTurnRole, { label: string; className: string }> = {
  user: { label: 'Cliente', className: 'bg-bg-surface-alt text-text-muted' },
  assistant: { label: 'Agente', className: 'bg-brand-vivid/10 text-brand-vivid' },
  tool: { label: 'Tool', className: 'bg-brand-navy/10 text-brand-navy' },
};

const formatDate = (dateString: string | null): string =>
  dateString
    ? new Date(dateString).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '—';

export interface ScenarioImportResult {
  turns: AgentTestTurn[];
  toolStubs: Record<string, unknown>;
}

interface ScenarioImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (result: ScenarioImportResult) => void;
  projectId: number;
  projectName?: string;
}

const ScenarioImportDialog = ({
  open,
  onOpenChange,
  onImport,
  projectId,
  projectName,
}: ScenarioImportDialogProps): JSX.Element => {
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState<ConversationImportCandidate[] | null>(null);
  const [conversation, setConversation] = useState<ConversationImportPreview | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());

  const [importConversation, { isFetching: isSearching }] = useLazyImportConversationQuery();

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setCandidates(null);
    setConversation(null);
    setSearchError(null);
    setSelected(new Set());
  }, [open]);

  const handleSearch = async (q: string): Promise<void> => {
    setSearchError(null);
    setCandidates(null);
    try {
      const response = await importConversation({ projectId, q: q.trim() }).unwrap();
      if (response.type === 'candidates') {
        setConversation(null);
        setCandidates(response.candidates);
        return;
      }
      setConversation(response.conversation);
      setSelected(new Set(response.conversation.turns.map((_, index) => index)));
    } catch (err) {
      setConversation(null);
      const status = typeof err === 'object' && err !== null ? (err as { status?: unknown }).status : undefined;
      setSearchError(status === 404 ? NOT_FOUND_MESSAGE : extractErrorMessage(err, SEARCH_ERROR_FALLBACK));
    }
  };

  const toggleTurn = (index: number, checked: boolean): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(index);
      else next.delete(index);
      return next;
    });
  };

  const selectedTurns = conversation ? conversation.turns.filter((_, index) => selected.has(index)) : [];
  const selectedStubCount = selectedTurns.filter(
    (turn) => turn.role === 'tool' && turn.toolResult !== undefined
  ).length;

  const selectedToolCounts = new Map<string, number>();
  for (const turn of selectedTurns) {
    if (turn.role === 'tool' && turn.toolName) {
      selectedToolCounts.set(turn.toolName, (selectedToolCounts.get(turn.toolName) ?? 0) + 1);
    }
  }
  const duplicatedSelectedTools = [...selectedToolCounts.entries()]
    .filter(([, count]) => count > 1)
    .map(([name]) => name);

  const handleImport = (): void => {
    if (!conversation || selectedTurns.length === 0) return;

    const turns: AgentTestTurn[] = selectedTurns.map((turn) => {
      if (turn.role !== 'tool') {
        return { role: turn.role, content: turn.content };
      }
      const resultContent =
        typeof turn.toolResult === 'string' ? turn.toolResult : JSON.stringify(turn.toolResult ?? {});
      return {
        role: turn.role,
        content: resultContent,
        ...(turn.toolName ? { toolName: turn.toolName } : {}),
        ...(turn.toolCallId ? { toolCallId: turn.toolCallId } : {}),
      };
    });

    const toolStubs: Record<string, unknown> = {};
    for (const turn of selectedTurns) {
      if (turn.role === 'tool' && turn.toolName && turn.toolResult !== undefined) {
        toolStubs[turn.toolName] = turn.toolResult;
      }
    }

    onImport({ turns, toolStubs });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importar conversación</DialogTitle>
          <DialogDescription>
            Trae una conversación real y conviértela en los turnos del escenario. Las tool calls se importan con su
            resultado real como stub, y todo queda editable.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-text-muted"
              aria-hidden
            />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  if (query.trim().length > 0 && !isSearching) void handleSearch(query);
                }
              }}
              className="pl-9"
              placeholder="ID del cliente o teléfono del lead"
              aria-label="ID del cliente o teléfono del lead"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => void handleSearch(query)}
            disabled={query.trim().length === 0 || isSearching}
          >
            {isSearching ? 'Buscando…' : 'Buscar'}
          </Button>
        </div>

        {searchError ? (
          <div role="alert" className="rounded-md border border-error/30 bg-error-light px-3 py-2 text-sm text-error">
            {searchError}
          </div>
        ) : null}

        {candidates ? (
          <div className="flex flex-col gap-2">
            <p className="m-0 text-sm font-semibold text-text-default">
              Se encontraron {candidates.length} leads con ese teléfono. Elige uno:
            </p>
            {candidates.map((candidate) => (
              <button
                key={candidate.clientId}
                type="button"
                onClick={() => void handleSearch(String(candidate.clientId))}
                className="flex items-center gap-2 rounded-lg border border-border-subtle bg-bg-surface px-3 py-2
                  text-left transition-colors hover:border-brand-vivid"
              >
                <User className="size-4 shrink-0 text-text-muted" aria-hidden />
                <span className="text-xs font-medium text-text-default">
                  {candidate.name ?? candidate.phone ?? `#${candidate.clientId}`}
                </span>
                <span className="text-xs text-text-muted">
                  #{candidate.clientId}
                  {candidate.messagesCount !== null ? ` · ${candidate.messagesCount} mensajes` : ''} · último mensaje{' '}
                  {formatDate(candidate.lastMessageDate)}
                </span>
              </button>
            ))}
          </div>
        ) : null}

        {conversation ? (
          <>
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border-subtle bg-bg-surface-raised px-3 py-2">
              <User className="size-4 shrink-0 text-text-muted" aria-hidden />
              <span className="text-xs font-medium text-text-default">
                {conversation.client.name ?? conversation.client.phone ?? `#${conversation.client.clientId}`}
                {conversation.client.name && conversation.client.phone ? ` · ${conversation.client.phone}` : ''}
              </span>
              <span className="text-xs text-text-muted">
                {formatDate(conversation.client.lastMessageDate)} · {conversation.totalMessages} mensajes
                {projectName ? ` · ${projectName}` : ''}
                {conversation.truncated ? ' · conversación truncada a 1000 mensajes' : ''}
              </span>
            </div>

            <div className="flex items-center justify-between gap-2">
              <p className="m-0 text-sm font-semibold text-text-default">Selecciona los turnos a importar</p>
              <span className="text-xs text-text-muted">
                {selectedTurns.length} de {conversation.turns.length} seleccionados
              </span>
            </div>

            <div className="flex max-h-[300px] flex-col overflow-y-auto rounded-lg border border-border-subtle">
              {conversation.turns.map((turn, index) => {
                const chip = ROLE_CHIPS[turn.role];
                const isSelected = selected.has(index);
                return (
                  <div
                    key={index}
                    className={cn(
                      'flex items-start gap-2.5 px-3 py-2.5',
                      index < conversation.turns.length - 1 && 'border-b border-border-subtle'
                    )}
                  >
                    <span className="mt-0.5">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => toggleTurn(index, checked === true)}
                        aria-label={`Incluir turno ${index + 1}`}
                      />
                    </span>
                    <span
                      className={cn(
                        'inline-flex w-16 shrink-0 justify-center rounded-full px-2 py-0.5 text-[0.7rem] font-semibold',
                        chip.className
                      )}
                    >
                      {chip.label}
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <p
                        className={cn(
                          'm-0 text-xs break-words',
                          turn.role === 'tool' && 'font-mono text-[0.7rem]',
                          isSelected ? 'text-text-default' : 'text-text-muted'
                        )}
                      >
                        {turn.content}
                      </p>
                      {turn.role === 'tool' ? (
                        <span
                          className={cn(
                            'flex items-center gap-1 text-[0.7rem]',
                            turn.outdated ? 'text-warning' : 'text-text-muted'
                          )}
                        >
                          <Wrench className="size-3" aria-hidden />
                          {turn.outdated
                            ? 'resultado no disponible (outdated) → stub vacío editable'
                            : '→ el resultado real se agrega como stub'}
                        </span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>

            {conversation.warnings.piiReplacements > 0 ? (
              <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning-light px-3 py-2">
                <ShieldAlert className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
                <p className="m-0 text-sm text-text-default">
                  Se reemplazaron {conversation.warnings.piiReplacements} teléfonos/emails por placeholders. Revisa y
                  edita los turnos antes de guardar.
                </p>
              </div>
            ) : null}

            {duplicatedSelectedTools.length > 0 ? (
              <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning-light px-3 py-2">
                <Wrench className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
                <p className="m-0 text-sm text-text-default">
                  Tools repetidas ({duplicatedSelectedTools.join(', ')}): el resultado de la última llamada seleccionada
                  quedará como stub del run.
                </p>
              </div>
            ) : null}

            <div className="flex items-start gap-2 rounded-md border border-brand-vivid/30 bg-surface-info px-3 py-2">
              <Info className="mt-0.5 size-4 shrink-0 text-brand-vivid" aria-hidden />
              <p className="m-0 text-sm text-text-default">
                Se importarán {selectedTurns.length} turnos
                {selectedStubCount > 0
                  ? ` y ${selectedStubCount} tool stub${selectedStubCount === 1 ? '' : 's'} con el resultado real`
                  : ''}
                . Podrás editarlos antes de guardar.
              </p>
            </div>
          </>
        ) : null}

        {!conversation && !candidates && !searchError ? (
          <p className="m-0 text-xs text-text-muted">
            Pega el ID del cliente o el teléfono del lead. Lo encuentras en el detalle del lead o en la URL de la
            conversación.
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleImport} disabled={selectedTurns.length === 0}>
            <MessageSquarePlus className="size-4" aria-hidden />
            {selectedTurns.length > 0 ? `Importar ${selectedTurns.length} turnos` : 'Importar turnos'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScenarioImportDialog;
